import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { UserPersona } from '../services/userService';
import useAppState from '../common/AppState';

// Define the context type
interface UserContextType {
  activePersona: UserPersona;
  setActivePersona: (persona: UserPersona) => void;
  allPersonas: UserPersona[];
}

// Create the context with a default value
const UserContext = createContext<UserContextType | undefined>(undefined);

// Default persona when no user is logged in
const defaultPersona: UserPersona = {
  id: 'default',
  name: 'Guest User',
  email: 'guest@example.com',
  role: 'Loan Advisor',
  avatar: '/avatars/default_avatar.png',
  title: 'Guest',
  description: 'Not logged in',
};

// Provider component
export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { profile, orgUser } = useAppState();

  // Build the active persona from real profile data
  const activePersona: UserPersona = useMemo(() => {
    if (!profile || !orgUser) {
      if (import.meta.env.DEV) console.log('👤 UserContext: No profile/orgUser, using default persona');
      return defaultPersona;
    }

    if (import.meta.env.DEV) console.log('👤 UserContext: Building persona from real profile', { profile, orgUser });

    // Map org_users role (or RBAC role_code) to UserRole type
    // VS_SUPER_ADMIN supersedes all FI roles — maps to CEO with full workbench access
    const roleMap: Record<string, UserPersona['role']> = {
      // RBAC Phase 4 role_codes
      VS_SUPER_ADMIN: 'CEO',
      VS_SUPPORT: 'CEO',
      VS_COMPLIANCE: 'CEO',
      VS_ENGINEERING: 'CEO',
      VS_PRODUCT: 'CEO',
      VS_EXECUTIVE: 'CEO',
      FI_ADMIN: 'CEO',
      LENDER_MANAGER: 'VP of Lending',
      LOAN_OFFICER: 'Loan Advisor',
      COMPLIANCE_AUDITOR: 'Loan Advisor',
      OPERATIONS_ANALYST: 'Loan Advisor',
      // Legacy role strings (fallback)
      admin: 'CEO',
      analyst: 'VP of Lending',
      viewer: 'Loan Advisor',
      auditor: 'Loan Advisor',
    };

    const mappedRole = roleMap[orgUser.role] || 'Loan Advisor';

    // VS_SUPER_ADMIN flag — used downstream to unlock all workbench views
    const isSuperUser = orgUser.role_code === 'VS_SUPER_ADMIN' ||
                        orgUser.role === 'VS_SUPER_ADMIN' ||
                        (orgUser.org_type === 'INTERNAL');

    return {
      id: profile.user_id,
      name: `${orgUser.first_name} ${orgUser.last_name}`,
      email: orgUser.email,
      role: mappedRole,
      avatar: profile.user_metadata?.avatarUrl || '/avatars/default_avatar.png',
      title: orgUser.role.charAt(0).toUpperCase() + orgUser.role.slice(1),
      description: orgUser.org_name || 'Organization',
      isSuperUser,
    };
  }, [profile, orgUser]);

  // setActivePersona is a no-op now - we use the logged-in user
  const setActivePersona = (_persona: UserPersona) => {
    if (import.meta.env.DEV) console.log('👤 UserContext: setActivePersona called but ignored - using logged-in user');
  };

  // allPersonas now only contains the current user
  const allPersonas = useMemo(() => [activePersona], [activePersona]);

  // Provide the context value
  const contextValue = {
    activePersona,
    setActivePersona,
    allPersonas,
  };

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

// Custom hook to use the user context
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};